import { InterfaceComponentOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { ComponentLogic } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-logic/base/component-logic';
import { IBaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/base-component.interface';
import { getTouchOrMouseInfo } from '@src/slide/infrastructure/module/independent/mobile/ipad-compatible';
import { InteractionEvent } from '@tencent/alloy-renderer';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { EditorController } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller';
import mouseEventSourceDetector from '@src/slide/infrastructure/module/independent/mobile/mouse-event-source-detector';
import type { TextElement } from '@tencent/alloy-editor';
import { ITextEditor, ITextEditorEventHandlers } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';

interface IDisposable {
    dispose(): void;
}

export class TextEditorLogic extends ComponentLogic {
    declare protected component: ITextEditor;
    declare protected componentOptions: InterfaceComponentOptions;
    // 标记当前 textEditor 是否触发了 mousedown，主要是在 mousemove 的时候做判断
    private isTouchStart = false;
    private isMouseDown: boolean;
    private isAutoEventOn = false;
    private controller: EditorController;

    // 事件取消注册回调
    private mouseDownOff: IDisposable;
    private clickOff: IDisposable;
    private touchStartOff: IDisposable;
    public get isEditing(): boolean {
        return this.controller.isEditing;
    }

    public constructor(componentOptions: InterfaceComponentOptions, component: IBaseComponent) {
        super(componentOptions, component);
        const eventHandlers: ITextEditorEventHandlers = {
            handleTouchMove: this.handleTouchMove,
            handleTouchEnd: this.handleTouchEnd,
            handleMouseMove: this.handleMouseMove,
            handleMouseUp: this.handleMouseUp,
            handleContextMenu: this.handleContextMenu,
            handleMouseDown: this.handleMouseDown,
            handleTouchStart: this.handleTouchStart,
            handleClick: this.handleClick,
            initEditorController: this.initEditorController,
            getEditorController: this.getEditorController,
            handleEditorFocus: this.handleEditorFocus,
            handleEditorBlur: this.handleEditorBlur,
            handleKeyDown: this.handleKeyDown,
            handleBeforeInput: this.handleBeforeInput,
            registerEvent: this.registerEvent,
            unregisterEvent: this.unregisterEvent,
        };
        this.component.setEventHandlers(eventHandlers);
        this.initEditorController();
        this.registerEvent();
    }

    public initEditorController = () => {
        this.controller = new EditorController({
            getAlloyEditor: () => this.component.editor,

            getProps: () => this.component.props,

            getTextRect: () => this.component.editor.textRect,

            isReadOnly: this.component.isReadOnly,

            getAppPosition: this.component.getAppPosition,

            updateChangeFlag: (changeFlag) => {
                this.component.onChangeFlagChange?.(changeFlag);
            },

            onChange: (...params) => {
                this.component.props.onChange?.(...params);
                // 重置「文本是否有改变」的标记，在触发文字保存之后执行，避免重复保存
                this.controller.resetValueChangeFlag();
            },
            getEditorHeightByParagraphList: this.component.getEditorHeightByParagraphList,
            getAllStylesSync: this.component.getAllStylesSync,
            isExpanded: this.component.isExpanded,
        }, this.component.editorModel, this.component.services);
    };

    private getEditorController = () => this.controller;

    /**
     * 加载动态注册的事件
     */
    private autoRegisterEvent = () => {
        if (!this.isAutoEventOn) {
            if (browser.isPad) {
                // ipad 客户端需要兼容鼠标，但是操作逻辑仍然是和触摸一样的逻辑
                document.addEventListener('mousemove', this.handleTouchMove, true);
                document.addEventListener('mouseup', this.handleTouchEnd, true);
            } else {
                // mousemove 需要监听全局，否则超出文本范围后鼠标拖拽选区无反应
                // useCapture 参数为 true 是为了避免有的元素阻止冒泡之后就不会触发 document 上的对应事件
                document.addEventListener('mousemove', this.handleMouseMove, true);
                // 超出文本框之后触发 mouseup 也要执行 handleMouseUp
                document.addEventListener('mouseup', this.handleMouseUp, true);
            }
            document.addEventListener('touchmove', this.handleTouchMove, true);
            document.addEventListener('touchend', this.handleTouchEnd, true);
            document.addEventListener('touchcancel', this.handleTouchEnd, true);
            document.addEventListener('contextmenu', this.handleContextMenu, true);
            this.isAutoEventOn = true;
        }
    };

    /**
     * 取消动态注册的事件
     */
    private autoUnRegisterEvent = () => {
        if (this.isAutoEventOn) {
            if (browser.isPad) {
                document.removeEventListener('mousemove', this.handleTouchMove, true);
                document.removeEventListener('mouseup', this.handleTouchEnd, true);
            } else {
                document.removeEventListener('mousemove', this.handleMouseMove, true);
                document.removeEventListener('mouseup', this.handleMouseUp, true);
            }
            document.removeEventListener('touchmove', this.handleTouchMove, true);
            document.removeEventListener('touchend', this.handleTouchEnd, true);
            document.removeEventListener('touchcancel', this.handleTouchEnd, true);
            document.removeEventListener('contextmenu', this.handleContextMenu, true);
            this.isAutoEventOn = false;
        }
    };

    private handleClick = (e: InteractionEvent) => {
        this.controller.handleClick(e);
    };

    private handleTouchStart = (e: InteractionEvent) => {
        // 非编辑态不阻止默认事件，可以正常滚动
        // 非滚动模式的编辑态阻止默认事件，避免拖拽光标过程中触发滚动
        if (!this.component.props.supportScrollMobile && this.isEditing) {
            // 不阻止默认事件的话，移动端长按会失焦
            e.preventDefault();
        }
        // 滚动模式只在点在光标上时阻止默认事件
        if (this.component.props.supportScrollMobile) {
            if (this.isCloseCaret(e)) {
                e.preventDefault();
            }
        }

        if (!this.component.editor) {
            this.component.editorContainer.initEditor();
        }
        this.autoRegisterEvent();
        if (this.component.props.supportScrollMobile || this.isEditing) {
            this.isTouchStart = true;
            this.controller.handleTouchStart(e);
        }
    };

    private handleTouchMove = (e) => {
        if (this.isTouchStart) {
            this.controller.handleTouchMove(e);
        }
    };

    private handleTouchEnd = (e) => {
        e.preventDefault();
        // handleTouchEnd会获取光标，非编辑权限不执行
        if (this.isTouchStart
            && (this.isEditing || this.component.props.supportScrollMobile)
            && this.component.props.editable) {
            const { x, y } = this.component.getAppPosition();
            const { scrollTop = 0, scrollLeft = 0 } = document.documentElement;
            this.controller.handleTouchEnd({
                posXOfWorld: getTouchOrMouseInfo(e).pageX - (x + scrollLeft),
                posYOfWorld: getTouchOrMouseInfo(e).pageY - (y + scrollTop),
            });
        }
        this.isTouchStart = false;
        this.autoUnRegisterEvent();
    };

    private handleMouseMove = (e: MouseEvent) => {
        if (this.isMouseDown) {
            this.controller.handleMouseMove(e);
        }
    };

    private handleMouseUp = () => {
        this.isMouseDown = false;
        this.controller.handleMouseUp();
        // 在表格选中多个单元格的时候，会手动触发blur，导致mouseUp等事件不会触发。
        // 所以这里改到up中去解绑事件。整个事件绑定为：down的时候绑定，up的时候解绑
        this.autoUnRegisterEvent();
    };

    /**
     * 注意：由于editController在获取焦点后立即加载，立即执行的事件editController不会触发。
     * @param e
     */
    private handleMouseDown = (e: InteractionEvent) => {
        this.isMouseDown = true;
        // fix: 表格光标丢失的问题
        e.preventDefault();
        if (!this.component.editor) {
            this.component.editorContainer.initEditor();
        }
        this.autoRegisterEvent(); // 查看态也有选择事件，所以mouseDown的时候注册
        this.controller.handleMouseDown(e);
    };

    private handleContextMenu = (e: Event) => {
        e.preventDefault();
        if (this.isEditing) {
            /**
             * 移动端长按会触发contextMenu事件导致输入框失焦
             * 此前曾在touchStart内执行preventDefault避免此问题，但会导致该文本无法滚动
             * 因此参考melo的做法，在触发contextMenu时主动focus。
             */
            this.component.setFocus();
        }
    };

    private isCloseCaret = (e: InteractionEvent) => {
        const selectionRect = this.component.getExpandedRect();
        if (!selectionRect) {
            return false;
        }
        if (e.worldX > selectionRect.right + 8 || e.worldX < selectionRect.left - 8) {
            return false;
        }
        if (e.worldY > selectionRect.bottom + 8 || e.worldY < selectionRect.top - 8) {
            return false;
        }
        return true;
    };

    private handleEditorFocus = () => {
        this.controller.onFocus();
        this.controller.enterEditing();
        this.component.services.editingStateManager?.updateElementCropState(false);
    };

    private handleEditorBlur = () => {
        this.controller.exitEditing();
    };

    private handleKeyDown = (e: KeyboardEvent): boolean => !!this.controller?.handleKeyDown(e);

    private handleBeforeInput = (text: string, textElement: TextElement): Partial<TextElement> | void => {
        const value = this.controller.handleBeforeInput(text, textElement);
        return value;
    };

    private registerEvent = () => {
        /**
         * 移动端不注册 onClick 事件，有两个原因
         * 1. 移动端不需要 onclick 事件
         * 2. ios 上有一个诡异的 bug。新建 slide 页面，双击进入第二个 placeholder 的编辑态，点击 placeholder 下方失去焦点，
         * 偶尔会出现失焦之后再次自动聚焦的问题，排查了下是触发了当前组件的 click 事件，但是当时点击的地方并不在当前组件区域内
         */
        if (!browser.isMobile) {
            this.clickOff = this.component.editorContainer.onClick(this.handleClick || (() => {}));
        }
        if (this.component.isReadOnly() && !this.component.isSelectable()) return;
        if (browser.isMobile) {
            this.touchStartOff = this.component.editorContainer.onTouchStart(this.handleTouchStart || (() => {}));
            if (browser.isPad) {
                // 需要兼容鼠标，但是操作逻辑仍然是和触摸一样的逻辑
                this.mouseDownOff = this.component.editorContainer.onMouseDown(this.handleIpadMousedown);
            }
        } else {
            /**
             * 移动端不注册 mousedown 事件，有两个原因
             * 1. 移动端不需要 mousedown 事件
             * 2. 移动端偶现会触发 mousedown，导致进入编辑态，不符合预期
             */
            this.mouseDownOff = this.component.editorContainer.onMouseDown(this.handleMouseDown || (() => {}));
        }
    };

    private handleIpadMousedown = (event: InteractionEvent) => {
        if (mouseEventSourceDetector.isTriggerByTouch(event)) {
            return;
        }
        this.handleTouchStart(event);
    };

    private unregisterEvent = () => {
        this.mouseDownOff?.dispose();
        this.clickOff?.dispose();
        this.touchStartOff?.dispose();
    };
}
